import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ExpectedOrderDialog = ({ open, onOpenChange, item, onCreated }) => {
  const { api } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({
    expected_quantity: '',
    vendor_id: '',
    purchase_order_number: '',
    expected_arrival_date: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      expected_quantity: '',
      vendor_id: item.preferred_vendor_id || '',
      purchase_order_number: '',
      expected_arrival_date: '',
      notes: '',
    });
    api().get('/vendors').then(({ data }) => setVendors(data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [open, item?.id]);

  if (!item) return null;

  const submit = async (e) => {
    e.preventDefault();
    const qty = parseFloat(form.expected_quantity);
    if (!(qty > 0)) { toast.error('Expected quantity required'); return; }
    setBusy(true);
    try {
      await api().post('/expected-orders', {
        item_id: item.id,
        expected_quantity: qty,
        vendor_id: form.vendor_id || null,
        purchase_order_number: form.purchase_order_number || null,
        expected_arrival_date: form.expected_arrival_date || null,
        notes: form.notes || null,
      });
      toast.success('Order flagged — staff will see it on the dashboard until received');
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to flag order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="expected-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" /> Flag Order — {item.item_sku}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">
            Lets the team know inventory is on the way. Appears on the dashboard's "Open Orders" widget and pre-fills the Restock dialog when product arrives.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Expected Qty *</Label>
              <Input type="number" min={1} step="any" required
                value={form.expected_quantity}
                onChange={(e) => setForm({ ...form, expected_quantity: e.target.value })}
                className="rounded-sm mono h-9" data-testid="eo-quantity" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">PO Number</Label>
              <Input value={form.purchase_order_number}
                onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })}
                className="rounded-sm mono h-9" data-testid="eo-po" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Vendor</Label>
            <Select value={form.vendor_id || 'none'} onValueChange={(v) => setForm({ ...form, vendor_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="rounded-sm h-9" data-testid="eo-vendor">
                <SelectValue placeholder="Select vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expected Arrival</Label>
            <Input type="date" value={form.expected_arrival_date}
              onChange={(e) => setForm({ ...form, expected_arrival_date: e.target.value })}
              className="rounded-sm h-9" data-testid="eo-arrival" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="ETA, tracking, special instructions" className="rounded-sm text-xs" rows={2}
              data-testid="eo-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="eo-submit">
              {busy ? 'Flagging…' : 'Flag as Ordered'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExpectedOrderDialog;
